import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

import { BACKEND_URL } from "@/lib/backendUrl";
const API = `${BACKEND_URL}/api`;

// Owner-only: wipe customers / quotes / invoices to start afresh (keeps users, prices, website content)
export const DangerZone = () => {
  const { isOwner } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!isOwner) return null;
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/reset-data`, { confirm });
      toast.success("All data cleared — starting afresh", { description: `${data.removed.jobs} quotes · ${data.removed.customers} customers · ${data.removed.invoices} invoices removed` });
      setConfirm("");
      setAsk(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail });
      setBusy(false);
    }
  };
  return (
    <div data-testid="danger-zone" className="rounded-lg border border-rose-900/60 bg-rose-950/20 p-4 space-y-3">
      <p className="text-xs uppercase tracking-wider text-rose-300 font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Danger zone · 清除所有数据</p>
      <p className="text-xs text-slate-400">Permanently deletes <b className="text-slate-200">all customers, quotes/jobs and invoices</b> and restarts numbering. Users, prices, settings and website content are kept. This cannot be undone.</p>
      <div className="flex gap-2">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder="Type RESET to confirm" data-testid="danger-reset-confirm" className="bg-slate-950/60 border-rose-900/60 font-mono-num uppercase max-w-xs" />
        <Button type="button" onClick={() => setAsk(true)} disabled={confirm !== "RESET" || busy} data-testid="danger-reset-btn" className="bg-rose-600 hover:bg-rose-500 text-white font-bold disabled:opacity-40">Clear all data · 开始全新</Button>
      </div>
      <AlertDialog open={ask} onOpenChange={(o) => !busy && setAsk(o)}>
        <AlertDialogContent data-testid="danger-confirm-dialog" className="bg-[#111827] border-rose-900/60 text-slate-100 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2 text-rose-300"><AlertTriangle className="w-5 h-5" /> Are you absolutely sure? · 确定删除？</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Every customer, quote/job and invoice will be <b className="text-rose-300">permanently erased</b> and quote / invoice numbering restarts from 0001.
              There is no undo and no backup is taken by this action. Your login, prices, settings and website content stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="danger-confirm-cancel" className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel · 取消</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); reset(); }} disabled={busy} data-testid="danger-confirm-yes" className="bg-rose-600 hover:bg-rose-500 text-white font-bold">{busy ? "Clearing…" : "Yes, erase everything · 确定"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
